import { ExtendedPlatformContext } from '../../../../types/platformApi';
import { getAddUnifiedComponentsMethods } from './addComponents/addUnifiedComponents';
import { chainPromisesAsync } from './utils';
import { PageRef, CompRef } from '@wix/document-services-types';
import {
  createUnifiedComponentsInstallationError,
  errorTypeMapping,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';
import {
  AppData,
  UnifiedComponentsCollection,
  UnifiedWidget,
  BuilderComponent,
  Container,
  UnifiedComponentAddedToTheStageData,
} from '../../../../types/unifiedComponents';
import { BIReporter } from './bi';
import { HOME_PAGE_ID } from '../../intentInstallService';

export function installUnifiedComponents(
  context: ExtendedPlatformContext,
  biReporter: BIReporter,
  appData: AppData,
  collection: UnifiedComponentsCollection,
) {
  const addedToStage: UnifiedComponentAddedToTheStageData = {
    pages: {},
    widgets: {},
  };
  const homePage = context.documentServices.pages.getReference(
    context.documentServices.homePage.get(),
  ) as PageRef;
  homePage.pageId = homePage.id;
  const { pages, widgets, builderComponents } = collection;
  const components_list = Object.keys(collection.pages).concat(
    Object.keys(collection.widgets),
  );
  const { addPage, addWidget, addComponent } = getAddUnifiedComponentsMethods(
    context,
    appData,
  );

  function installUnifiedPages() {
    const addSinglePage = ([guid, container]: [string, Container]) =>
      new Promise<PageRef | undefined>((resolve, reject) => {
        addPage(container, widgets)
          .then((pageRef) => {
            if (pageRef) {
              addedToStage.pages[guid] = pageRef;
            }
            resolve(pageRef);
          })
          .catch((error: any) => {
            return reject(
              createUnifiedComponentsInstallationError(
                ErrorType.addingUnifiedComponentFailed,
                `Failed to add '${container.data.base?.name}' ${container.type} component`,
              )
                .withAppDefIds(appData.appDefinitionId)
                .withComponentGUID(guid, errorTypeMapping[container.type])
                .withParentError(error),
            );
          });
      });

    return chainPromisesAsync(Object.entries(pages), addSinglePage);
  }

  function checkIfShouldSkipWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return false;
    }

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetHasPageSelected = !!widgetPages[widget.widgetId];

    return !widgetHasPageSelected;
  }

  function getPageRefForWidget(widget: UnifiedWidget) {
    if (!context.intentInstallServices.shouldUseWidgetAppsConfiguration()) {
      return homePage;
    }

    const pagesData = context.documentServices.pages.getPagesData();

    const widgetPages = context.intentInstallServices.getWidgetsConfiguration();

    const widgetPage = pagesData.find(
      (page) => page.tpaPageId === widgetPages[widget.widgetId],
    )?.id;

    return widgetPage && widgetPage !== HOME_PAGE_ID
      ? (context.documentServices.pages.getReference(widgetPage) as PageRef)
      : homePage;
  }

  function addSingleWidget(widget: UnifiedWidget) {
    if (checkIfShouldSkipWidget(widget)) {
      return Promise.resolve();
    }

    const pageRef = getPageRefForWidget(widget);

    return new Promise<PageRef | void>((resolve, reject) =>
      addWidget(pageRef, widget)
        .then((addedWidget) => {
          if (addedWidget) {
            addedToStage.widgets[widget.widgetId] = addedWidget.widgetRef;
          }
          resolve(pageRef);
        })
        .catch((error) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.addingUnifiedComponentFailed,
              `Failed to add '${widget?.base?.name}' widget component`,
            )
              .withAppDefIds(appData.appDefinitionId)
              .withComponentGUID(widget.widgetId, 'unified-widget')
              .withParentError(error),
          ),
        ),
    );
  }

  function addSingleBuilderComponent(component: BuilderComponent) {
    const pageRef =
      (context.documentServices.pages.getCurrentPage() as PageRef) || homePage;

    return new Promise<CompRef | void>((resolve, reject) =>
      addComponent(pageRef, component)
        .then((widgetRef: void | CompRef) => {
          if (widgetRef) {
            addedToStage.widgets[component.componentId] = widgetRef;
          }
          resolve(widgetRef);
        })
        .catch((error) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.addingUnifiedComponentFailed,
              `Failed to add '${component.data.type}' builder component`,
            )
              .withAppDefIds(appData.appDefinitionId)
              .withComponentGUID(component.componentId, 'builder-component')
              .withParentError(error),
          ),
        ),
    );
  }

  function installUnifiedWidgets() {
    return new Promise<void>((resolve, reject) => {
      const widgetsArray = Object.values(widgets);
      if (widgetsArray.length === 0) {
        return resolve();
      }

      chainPromisesAsync(widgetsArray, addSingleWidget)
        .then(resolve)
        .catch(reject);
    });
  }

  function installBuilderComponents() {
    return new Promise<void>((resolve, reject) => {
      const componentsArray = Object.values(builderComponents);
      if (componentsArray.length === 0) {
        return resolve();
      }

      chainPromisesAsync(componentsArray, addSingleBuilderComponent)
        .then(resolve)
        .catch(reject);
    });
  }

  return biReporter.withBI(
    'NEW_COMPONENTS_INSTALL',
    {
      components_list,
      numberInstalledComponents: 0,
      appDefinitionId: appData.appDefinitionId,
    },
    function addUnifiedComponentsToStage() {
      return new Promise<UnifiedComponentAddedToTheStageData>(
        (resolve, reject) =>
          installUnifiedPages()
            .then(installUnifiedWidgets)
            .then(installBuilderComponents)
            .then(() => {
              resolve(addedToStage);
            })
            .catch(reject),
      );
    },
  );
}
